/* What needs attention, above the table that would otherwise have to be read to find it.
 *
 * Exceptions before normal states: a reader arrives with "did anything break", and a ledger sorted
 * worst-first answers that only after they have interpreted a column. This is the same finding as a
 * sentence, and every entry is a button that opens the drawer **on the tab that answers it** — a
 * score drop opens "Why it changed", a breach opens the decomposition, a stale evaluation opens the
 * run history. A feed that only selected the row would leave the reader to find the answer again.
 *
 * **Nothing is detected that was not read.** Three of the six detectors need movement, which is one
 * request per dataset; when those reads are off the feed says so rather than reporting a quiet
 * estate. "We found nothing" and "we did not look" are the two states this component most has to
 * keep apart.
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { DetectionThresholds } from '../model/regressions';
import type { DrawerTab, Regression } from '../model/types';

export interface RegressionFeedProps {
  readonly regressions: readonly Regression[];
  readonly thresholds: DetectionThresholds;
  /** False when movement has not been read, so the feed cannot claim completeness. */
  readonly movementRead: boolean;
  readonly onOpen: (datasetId: string, tab: DrawerTab) => void;
}

export function RegressionFeed({
  regressions,
  thresholds,
  movementRead,
  onOpen,
}: RegressionFeedProps): ReactElement {
  const { t } = useTranslation();

  return (
    <section className="lq-regress" aria-label={t('scoring.regression.region')}>
      <div className="lq-regress__head">
        <h2 className="lq-regress__title">{t('scoring.regression.title')}</h2>
        <span className="lq-regress__count">
          {t('scoring.regression.count', { count: regressions.length })}
        </span>
        <span className="lq-regress__spacer" />
        <span className="lq-regress__rules" title={t('scoring.regression.rulesHint')}>
          {t('scoring.regression.rules', {
            drop: thresholds.scoreDrop.toFixed(1),
            coverage: Math.round(thresholds.coverageDrop * 100),
            hours: thresholds.staleHours,
          })}
        </span>
      </div>

      {regressions.length === 0 ? (
        <p className="lq-regress__empty">
          {movementRead
            ? t('scoring.regression.none')
            : t('scoring.regression.notLookedFor')}
        </p>
      ) : (
        <>
          <ul className="lq-regress__list">
            {regressions.map((finding) => (
              <li key={`${finding.datasetId}:${finding.kind}`}>
                <button
                  type="button"
                  className="lq-regress__item"
                  onClick={() => {
                    onOpen(finding.datasetId, finding.tab);
                  }}
                >
                  <span
                    className={`lq-regress__bar lq-regress__bar--${finding.severity}`}
                    aria-hidden="true"
                  />
                  <span className="lq-regress__body">
                    <span className="lq-regress__dataset lq-mono">
                      {finding.datasetName}
                    </span>
                    <span className="lq-regress__message">
                      {/* The severity is in words as well as in the stripe: a coloured edge alone
                          is not a state a greyscale or colour-blind reader can read. */}
                      <span className="lq-visually-hidden">
                        {t(`scoring.regression.severity.${finding.severity}`)}{' '}
                      </span>
                      {t(finding.messageKey, finding.messageArgs)}
                    </span>
                    <span className="lq-regress__detail">
                      {t(finding.detailKey, finding.detailArgs)}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {!movementRead && (
            <p className="lq-regress__partial">{t('scoring.regression.partialScan')}</p>
          )}
        </>
      )}
    </section>
  );
}
